import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Select, Button, message, Spin, Alert } from 'antd';
import { FileWordOutlined } from '@ant-design/icons';
import MeService from '../../services/MeService';
import ProgramsService from '../../services/ProgramsService';
import MohReportDataService from '../../services/MohReportDataService';
import { logTrail, TRAIL_EVENTS } from '../../utils/activityTrail';

const { Option } = Select;
const FRAMEWORK_CODE = 'MOHPERF';

export default function MoHReportGenerate() {
    const [periods, setPeriods]         = useState([]);
    const [periodLabel, setPeriodLabel] = useState(null);
    const [units, setUnits]             = useState([]);
    const [programId, setProgramId]     = useState(null);
    const [loading, setLoading]         = useState(true);
    const [generating, setGenerating]   = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [fwRes, unitsRes] = await Promise.all([
                    MeService.getFrameworks(),
                    ProgramsService.getPrograms({ status: 1 }),
                ]);
                const fw = (fwRes.data?.data || []).find(f => f.code === FRAMEWORK_CODE);
                setUnits(unitsRes.data?.data || []);
                if (fw) {
                    const pRes = await MeService.getMePeriods(fw.id);
                    const ps = pRes.data?.data || [];
                    setPeriods(ps);
                    const cur = ps.find(p => p.is_current == 1);
                    setPeriodLabel(cur ? cur.label : (ps[0]?.label || null));
                } else {
                    message.error('MoH Reporting framework not found — run the module migration first');
                }
            } catch { message.error('Failed to load'); }
            finally { setLoading(false); }
        })();
    }, []);

    const download = async () => {
        if (!programId)   { message.warning('Select a submitting unit'); return; }
        if (!periodLabel) { message.warning('Select a reporting period'); return; }
        setGenerating(true);
        try {
            const res = await MohReportDataService.generateReport({ program_id: programId, period_label: periodLabel });
            const unit = units.find(u => u.id === programId);
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
            const url  = window.URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url;
            a.download = `moh_report_${(unit?.code||'unit').toLowerCase()}_${periodLabel.replace(/[^A-Za-z0-9]/g,'_')}.docx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            logTrail(TRAIL_EVENTS.REPORT_GENERATE, 'MoH Reporting',
                { unit: unit?.name, period: periodLabel }, `Generated report for ${unit?.name}`);
            message.success('Report downloaded');
        } catch (e) {
            message.error('Report generation failed — check that the unit has data for this period');
        } finally {
            setGenerating(false);
        }
    };

    if (loading) return <div style={{textAlign:'center',padding:60}}><Spin size="large"/></div>;

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{margin:0}}>📄 MoH Reporting — Generate Report</h4>
                    <small className="text-muted">Download the Technical Report as an editable Word document</small>
                </div>
            </div>
            <div className="page-pad">
                <Alert
                    type="info" showIcon style={{ marginBottom:16 }}
                    message="Single-unit report"
                    description="This generates the full Technical Report for one submitting unit — every indicator, finance line, HR cadre, clinical volume, and fleet entry for the period selected below. A consolidated national roll-up across all units is a separate, planned feature."
                />
                <Card size="small">
                    <Row gutter={16} align="bottom">
                        <Col span={10}>
                            <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Submitting Unit</label>
                            <Select showSearch optionFilterProp="children" style={{width:'100%'}}
                                placeholder="Select unit" value={programId} onChange={setProgramId}>
                                {units.map(u => <Option key={u.id} value={u.id}>{u.name} <span style={{color:'#aaa',fontSize:11}}>({u.code})</span></Option>)}
                            </Select>
                        </Col>
                        <Col span={8}>
                            <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Reporting Period</label>
                            <Select style={{width:'100%'}} placeholder="Select period" value={periodLabel} onChange={setPeriodLabel}>
                                {periods.map(p => <Option key={p.label} value={p.label}>{p.is_current==1?'★ ':''}{p.label}</Option>)}
                            </Select>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" icon={<FileWordOutlined/>} loading={generating}
                                onClick={download} style={{ background:'#2e75b6', borderColor:'#2e75b6', width:'100%' }}>
                                Download Report (Word)
                            </Button>
                        </Col>
                    </Row>
                </Card>
            </div>
        </div>
    );
}